// Tipus i models de dades per al Disseny de Bases de Dades (ER, Script, Relacional)

// -------------------------------------------------------------
// 1. MODEL ENTITAT-RELACIÓ (ER)
// -------------------------------------------------------------

export type ErCardinality = '1:1' | '1:N' | 'N:1' | 'N:N';
export type ErParticipation = '(0,1)' | '(1,1)' | '(0,N)' | '(1,N)';
export type ErHierarchyCoverage = 't' | 'p'; // Total / Parcial
export type ErHierarchyDisjoint = 'd' | 's'; // Disjunt / Solapat

export interface ErEntity {
    id: string;
    name: string;
    isWeak: boolean;
    x: number;
    y: number;
    width?: number;
    height?: number;
}

export interface ErRelationship {
    id: string;
    name: string;
    isWeak: boolean; // Relació identificativa per a entitats febles
    cardinality: ErCardinality; // 1:1, 1:N, N:N
    isReflexive?: boolean; // Relació reflexiva / recursiva (dibuix en doble arc)
    x: number;
    y: number;
    width?: number;
    height?: number;
}

export interface ErAttribute {
    id: string;
    parentId: string; // ID de l'entitat o relació a la qual pertany
    name: string;
    isPK: boolean;          // Clau Primària (Subratllat)
    isWeakPK: boolean;      // Clau Primària Dèbil / Discriminador (Discontinu)
    isMultivalued: boolean; // Multivaluat (Doble òval)
    isDerived: boolean;     // Derivat (Òval discontinu)
    x: number;
    y: number;
    width?: number;
    height?: number;
}

export interface ErConnection {
    id: string;
    from: string; // ID entitat o relació o atribut o jerarquia
    to: string;   // ID entitat o relació o atribut o jerarquia
    participation?: ErParticipation; // (0,1), (1,1), (0,N), (1,N) - especialment entre Entitat i Relació
    role?: string; // Opcional (nom del rol en relacions recursives)
    isReflexive?: boolean; // Forçar curvatura en arc
    curveOffset?: number; // Curvatura personalitzada de l'arc (ex: -50 o +50)
}

export interface ErHierarchy {
    id: string;
    superEntityId: string; // Entitat pare
    subEntityIds: string[]; // Entitats filles
    coverage: ErHierarchyCoverage; // 't' (total) | 'p' (parcial)
    disjointness: ErHierarchyDisjoint; // 'd' (disjunta) | 's' (solapada)
    x: number;
    y: number;
}

export interface ErDiagramData {
    type: 'ER';
    version: number;
    entities: ErEntity[];
    relationships: ErRelationship[];
    attributes: ErAttribute[];
    connections: ErConnection[];
    hierarchies: ErHierarchy[];
}

export const createDefaultErDiagram = (): ErDiagramData => ({
    type: 'ER',
    version: 1,
    entities: [],
    relationships: [],
    attributes: [],
    connections: [],
    hierarchies: []
});

// -------------------------------------------------------------
// 2. MODEL SCRIPT RELACIONAL
// -------------------------------------------------------------

export interface ScriptAttribute {
    name: string;
    isPK: boolean;
    fkTargetTable?: string | null;
}

export interface ScriptTable {
    name: string;
    attributes: ScriptAttribute[];
}

export interface ScriptRelationalData {
    type: 'Script';
    version: number;
    tables: ScriptTable[];
}

// -------------------------------------------------------------
// 3. MODEL RELACIONAL (TAULES AMB TIPUS I FKs)
// -------------------------------------------------------------

export interface RelationalColumn {
    id: string;
    name: string;
    type: string; // VARCHAR, INT, DECIMAL, DATE, TEXT, BLOB, etc.
    isPK: boolean;
    isNotNull?: boolean;
    isUnique?: boolean;
}

export interface RelationalTable {
    id: string;
    name: string;
    x: number;
    y: number;
    columns: RelationalColumn[];
}

export interface RelationalForeignKey {
    id: string;
    fromTable: string;
    fromColumn: string;
    toTable: string;
    toColumn: string;
}

export interface RelationalDiagramData {
    type: 'Relacional';
    version: number;
    tables: RelationalTable[];
    foreignKeys: RelationalForeignKey[];
}
